import customFetch, { handle423 } from "./helper"
import { User } from "@/types/users"
import { removeActiveBlogId, removeJwtCookies } from "../../util/cookies"
import {
  Blog,
  ErrorResponse,
  Subscription,
  SuccessResponse,
  BlogSubscriptionAndMembership,
} from "@types"
import { identifySentryUser } from "../utils/sentry"
import { setDatadogUser } from "../utils/datadog"

const API = "/users"

/**
 * Creates a user record after successful Privy user creation.
 *
 * Also "refreshes" the privy-id-token so it will contain the new
 * custom_metadata.
 */
export async function createUser(): Promise<User> {
  const response = await customFetch<User>(
    `${API}/create`,
    { method: "POST" },
    {
      // Don't care about impersonation here.
      ignoreImpersonation: true,
    }
  )

  return response
}

/**
 * Fetches the logged-in user.
 *
 * Also "refreshes" their JWT, to keep them logged in.
 */
export async function getLoggedInUser(): Promise<User> {
  const response = await customFetch<User>(
    `${API}/refresh`,
    {
      // Ensure we're not using any cached data at any level
      cache: "no-store",
      next: { revalidate: 0 },
    },
    {
      // Don't care about impersonation here.
      ignoreImpersonation: true,
    }
  )

  if (response) {
    identifySentryUser(response)
    setDatadogUser(response)
  }

  // User is disabled? Redirect them
  if (response?.disabled) {
    await handle423()
  }

  return response
}

// TODO: Remove in future PR
export async function loginUser(
  userParams: any
): Promise<User | ErrorResponse> {
  try {
    const response = await customFetch<User>(`${API}/login`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    console.log("Login response: ", response)

    return response
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return {
        msg: "Incorrect email or password. Please try again.",
      }
    }
  }

  return { msg: "Incorrect email or password. Please try again." }
}

/**
 * Logs the user out.
 * This just deletes their JWT.
 * We need to redirect them on the FE if we wish.
 */
export async function logoutUser(): Promise<User> {
  const response = await customFetch<User>(`${API}/logout`, { method: "POST" })

  removeJwtCookies()
  removeActiveBlogId()

  return response
}

/**
 * For the logged in user, fetches all blogs
 * they're a team member of.
 */
export async function getUsersTeamBlogs(): Promise<Array<Blog>> {
  const response = await customFetch<Array<Blog>>(`${API}/team/blogs`)

  return response
}

/**
 * Fetches all user's subscriptions to other publications
 */
export async function getAllUsersSubscriptions(): Promise<
  Array<BlogSubscriptionAndMembership>
> {
  const response = await customFetch<Array<BlogSubscriptionAndMembership>>(
    `${API}/subscriptions`
  )

  return response
}

// TODO: remove in future PR
export async function setPasswordAndLoginUser(
  userParams: any
): Promise<User | ErrorResponse> {
  try {
    const response = await customFetch<User>(`${API}/setPasswordAndLogin`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    // TODO: If the user owns a custom domain (eg avc.xyz), we should ideally set the cookie there too, even if they're currently logging on on paragraph.com
    await customFetch<User>(`${API}/login`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    return response
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return {
        msg: "Email or password is incorrect",
      }
    }
    return { msg: "An error occurred setting password and logging in." }
  }
}

/**
 * Fetches a user's subscription to a blog.
 * @param blogId: The blog ID to fetch the subscription for.
 * @param email: The email to check the subscription for. If not provided, it will check for the logged-in user.
 */
export async function getUsersSubscriptionToBlog(
  blogId: string,
  email?: string
): Promise<Subscription | false | ErrorResponse> {
  const response = await customFetch<Subscription | false | ErrorResponse>(
    `${API}/subscriptions/${blogId}${email ? `?${new URLSearchParams({ email }).toString()}` : ""}`,
    {},
    {
      ignoreImpersonation: true,
    }
  )

  return response
}

/**
 * Unsubscribes user from a subscription
 */
export async function deleteSubscription(
  subscriptionId: string
): Promise<SuccessResponse | ErrorResponse> {
  try {
    const response = await customFetch<SuccessResponse | ErrorResponse>(
      `/blogs/subscriptions/${subscriptionId}`,
      {
        method: "DELETE",
      }
    )

    return response
  } catch (error) {
    console.error("Error unsubscribing:", error)
    return { msg: "Failed to unsubscribe. Please try again." }
  }
}

/**
 * Permanently deletes a user account
 */
export async function permanentlyDeleteUserAccount(
  userId: string,
  feedbackMsg: string
): Promise<boolean | ErrorResponse> {
  try {
    const response = await customFetch<boolean | ErrorResponse>(
      `${API}/user/${userId}?feedback=${feedbackMsg}`,
      { method: "DELETE" }
    )

    // Ensure all auth cookies are removed on successful delete
    if (response === true) {
      removeJwtCookies() // This now also removes userId cookie
    }

    return response
  } catch (error) {
    console.error("Error deleting account:", error)
    return { msg: "Failed to delete account. Please try again later." }
  }
}

/**
 * Updates author details for a user
 */
export async function updateUserAuthorDetails(
  userId: string,
  authorName?: string,
  authorBio?: string,
  social?: {
    twitter?: string
    github?: string
    farcaster?: string
    facebook?: string
    instagram?: string
  }
): Promise<SuccessResponse | ErrorResponse> {
  try {
    const response = await customFetch<SuccessResponse | ErrorResponse>(
      `${API}/${userId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ authorName, authorBio, social }),
      }
    )

    return response
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return {
        msg: err.response.data.msg,
      }
    }

    console.error("Error updating author details:", err)
    return {
      msg: "An error occurred updating your author details. Please try again.",
    }
  }
}

/**
 * Changes user avatar
 */
export async function changeAvatar(
  file: File
): Promise<SuccessResponse | ErrorResponse> {
  try {
    const formData = new FormData()
    formData.append("file", file)

    const response = await customFetch<SuccessResponse | ErrorResponse>(
      `${API}/avatar`,
      {
        method: "POST",
        body: formData,
      }
    )

    return response
  } catch (error) {
    console.error("Error changing avatar:", error)
    return { msg: "Failed to update avatar. Please try again." }
  }
}

/**
 * Removes user avatar
 */
export async function removeAvatar(): Promise<SuccessResponse | ErrorResponse> {
  try {
    const response = await customFetch<SuccessResponse | ErrorResponse>(
      `${API}/avatar`,
      {
        method: "DELETE",
      }
    )

    return response
  } catch (error) {
    console.error("Error removing avatar:", error)
    return { msg: "Failed to remove avatar. Please try again." }
  }
}
